import * as React from 'react';

import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { useRetracked } from 'js/lib/retracked';
import type { TrackingData } from 'js/lib/retracked';

import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import AddCoursesDialog from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/AddCoursesDialog';
import { useCoursePlanningDialog } from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/context/CoursePlanningDialogContext';
import {
  getPlannableCourseIdsFromDegreeCurriculum,
  updateCurriculumInCache,
} from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/lib/coursePlanningUtils';
import { useLearnerPlanStoreContext } from 'bundles/degree-home/components/v3-dpm/context/LearnerPlanStoreContext';
import type {
  UpdateTermMutation as UpdateTermMutationData,
  UpdateTermMutationVariables as UpdateTermVariables,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/updatePlannedTermMutation';
import UpdateTermMutation from 'bundles/degree-home/components/v3-dpm/lib/__queries__/updatePlannedTermMutation.graphql';
import { isStringDefined } from 'bundles/degree-home/components/v3-dpm/lib/otherUtils';
import useCurriculum from 'bundles/degree-home/components/v3-dpm/lib/useCurriculum';
import useDegreePlan from 'bundles/degree-home/components/v3-dpm/lib/useDegreePlan';

type AddCoursesToTermDialogContextType = {
  openAddCoursesDialog: (estimatedTermId: string) => void;
};

const AddCoursesToTermDialogContext = React.createContext<AddCoursesToTermDialogContextType | undefined>(undefined);

export const useAddCoursesToTermDialog = () => {
  const context = React.useContext(AddCoursesToTermDialogContext);
  if (!context) {
    throw new Error('useAddCoursesToTermDialog must be used within an AddCoursesToTermDialogProvider');
  }
  return context;
};

export const AddCoursesToTermDialogProvider = ({ children }: React.PropsWithChildren) => {
  const [selectedSegmentCourseIds, setSelectedSegmentCourseIds] = React.useState<Set<string>>(new Set());
  const [currentEstimatedTermId, setCurrentEstimatedTermId] = React.useState<string | null>(null);

  const { data: curriculumData, refetch: refetchCurriculum } = useCurriculum();
  const { data: degreePlanData, refetch: refetchDegreePlan } = useDegreePlan();
  const currentLearnerPlanVersion = degreePlanData?.LearnerPlan?.queryPlan?.version;

  const academicTerms = curriculumData?.Degree?.queryById?.academicTermsPaginated?.elements ?? [];

  const plannableCourseIdsSet = React.useMemo(() => {
    const plannableCourseIds = getPlannableCourseIdsFromDegreeCurriculum(
      curriculumData?.Degree?.queryById?.curriculum?.curriculums ?? []
    );
    return new Set(plannableCourseIds);
  }, [curriculumData?.Degree?.queryById?.curriculum?.curriculums]);

  const matchedTermPlan = (degreePlanData?.LearnerPlan?.queryPlan?.learnerPlan ?? []).find(
    (plan) => plan.estimatedTermId === currentEstimatedTermId
  );
  const existingPlannedSegCourseIdsInSelectedTerm = (matchedTermPlan?.plannedSegmentCourses ?? [])
    .map((plannedCourse) => plannedCourse.personalizedSegmentCourse?.segmentCourse?.id)
    .filter(isStringDefined);

  const { degreeId } = useDegreeContext();
  const { requirementTypeData } = useLearnerPlanStoreContext();
  const { openCourseDialog } = useCoursePlanningDialog();
  const track = useRetracked();

  const isDialogOpen = currentEstimatedTermId !== null;

  const currentTerm = academicTerms.find((term) => term.estimatedTermId === currentEstimatedTermId);

  const onCheckboxGroupSelectionsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValues = new Set(selectedSegmentCourseIds);
    const currentlySelectedSegmentCourseId = event.target.value;
    if (newValues.has(currentlySelectedSegmentCourseId)) {
      newValues.delete(currentlySelectedSegmentCourseId);
    } else {
      newValues.add(currentlySelectedSegmentCourseId);
    }
    setSelectedSegmentCourseIds(newValues);
  };

  const openAddCoursesDialog = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (estimatedTermId: string) => setCurrentEstimatedTermId(estimatedTermId),
    []
  );

  const closeDialog = React.useCallback<() => $TSFixMe>(() => {
    setCurrentEstimatedTermId(null);
    setSelectedSegmentCourseIds(new Set());
  }, []);

  const [updateTermMutation, { loading: updateMutationLoading }] = useMutation<
    UpdateTermMutationData,
    UpdateTermVariables
  >(UpdateTermMutation, {
    context: { clientName: 'gatewayGql' },
  });

  const addCoursesHandler = (trackingData: TrackingData) => {
    if (currentEstimatedTermId && currentLearnerPlanVersion !== undefined) {
      updateTermMutation({
        variables: {
          input: {
            degreeId,
            estimatedTermId: currentEstimatedTermId,
            skipped: false, // TODO: handle skipped terms https://coursera.atlassian.net/browse/CPM-1915
            plannedSegmentCourses: [...existingPlannedSegCourseIdsInSelectedTerm, ...selectedSegmentCourseIds],
            version: currentLearnerPlanVersion,
          },
        },
        optimisticResponse: {}, // to make the cache update function work
        update: (cache) => {
          updateCurriculumInCache(cache, selectedSegmentCourseIds, 'PLANNED', degreeId);
        },
      })
        .then((response) => {
          const hasGqlError = response?.data?.LearnerPlan_UpdateTerm?.__typename !== 'LearnerPlan_LearnerPlanResult';
          if (!response || hasGqlError || response?.errors) {
            openCourseDialog(null, 'error');
          } else {
            track({ action: 'click', trackingName: 'add_courses', trackingData });
            refetchCurriculum();
            refetchDegreePlan();
          }
        })
        .catch((error) => {
          Sentry.captureException(error);
          openCourseDialog(null, 'error');
        });

      closeDialog();
    }
  };

  return (
    <AddCoursesToTermDialogContext.Provider value={{ openAddCoursesDialog }}>
      {children}
      {isDialogOpen && (
        <AddCoursesDialog
          currentEstimatedTermId={currentEstimatedTermId}
          selectedSegmentCourseIds={selectedSegmentCourseIds}
          plannableCourseIdsSet={plannableCourseIdsSet}
          updateMutationLoading={updateMutationLoading}
          termName={currentTerm?.estimatedTermName ?? ''}
          requirementTypeData={requirementTypeData}
          onCheckboxGroupSelectionsChange={onCheckboxGroupSelectionsChange}
          addCoursesHandler={addCoursesHandler}
          closeDialog={closeDialog}
        />
      )}
    </AddCoursesToTermDialogContext.Provider>
  );
};
